// Chế độ làm việc của Đại lý / CTV: "manage" (Quản lý mạng lưới) hoặc "sell" (Bán hàng — giống
// hệt giao diện Shop). docs/KE_HOACH_CHIA_GIAO_DIEN_QUAN_LY_BAN_HANG.md.
//  · Đường dẫn quyết định chế độ khi trang chỉ thuộc một bên (/products → Bán hàng, /network →
//    Quản lý); trang dùng chung (Tiền, Bảng giá, Báo cáo, Cài đặt…) giữ chế độ đang dùng.
//  · Nhớ theo trình duyệt (localStorage); không đọc/ghi được thì vẫn chạy (mặc định Quản lý).
//  · `chosen` = người dùng đã tự chọn ít nhất một lần (màn "Bạn muốn làm gì?" không hiện nữa).
// Shop và Admin không có chế độ — luôn coi như "sell" / không đổi gì.
//  · Cấp trên có thể chỉ chọn một giao diện cho Đại lý / CTV lúc tạo (Shop.workModes — docs/KE_HOACH_CHON_GIAO_DIEN_CAP_DUOI.md):
//    khi đó không có công tắc, trang của giao diện đã tắt không vào được (máy chủ cũng chặn).

import { useSyncExternalStore } from 'react'

export type WorkMode = 'manage' | 'sell'

const MODE_KEY = 'st-web-work-mode'
const CHOSEN_KEY = 'st-web-work-mode-chosen'
export const DEFAULT_WORK_MODE: WorkMode = 'manage'

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}
function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* trình duyệt chặn lưu — vẫn đổi trong phiên này */
  }
}

const initial = typeof window === 'undefined' ? null : read(MODE_KEY)
let mode: WorkMode = initial === 'manage' || initial === 'sell' ? initial : DEFAULT_WORK_MODE
let chosen = typeof window !== 'undefined' && read(CHOSEN_KEY) === '1'
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

export function getWorkMode(): WorkMode {
  return mode
}

export function hasChosenWorkMode(): boolean {
  return chosen
}

/** `byUser` = người dùng tự chọn (công tắc / màn chọn); false = suy ra từ đường dẫn. */
export function setWorkMode(next: WorkMode, byUser = false) {
  const changed = next !== mode || (byUser && !chosen)
  mode = next
  write(MODE_KEY, next)
  if (byUser && !chosen) {
    chosen = true
    write(CHOSEN_KEY, '1')
  }
  if (changed) emit()
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function useWorkMode(): WorkMode {
  return useSyncExternalStore(subscribe, getWorkMode, () => DEFAULT_WORK_MODE)
}

export function useWorkModeChosen(): boolean {
  return useSyncExternalStore(subscribe, hasChosenWorkMode, () => true)
}

// Hiệu ứng "trượt theo hướng" khi BẤM công tắc (mở link thẳng thì không chạy): công tắc xếp
// Quản lý bên trái, Bán hàng bên phải → sang Bán hàng nội dung trượt vào từ phải, về Quản lý từ trái.
let switchDir: { dir: 'left' | 'right'; at: number } | null = null

export function markModeSwitch(next: WorkMode) {
  switchDir = { dir: next === 'sell' ? 'right' : 'left', at: Date.now() }
}

/** Lấy (một lần) hướng trượt của lần bấm công tắc vừa rồi; quá 1,5 giây coi như không còn. */
export function takeModeSwitchDir(): 'left' | 'right' | null {
  const d = switchDir && Date.now() - switchDir.at < 1500 ? switchDir.dir : null
  switchDir = null
  return d
}

// Trang Gom Đơn (/inbox; /comments, /chat-channels là đường cũ chuyển về /inbox) chỉ có trong menu Bán hàng
const SELL_PREFIXES = ['/app', '/orders', '/products', '/customers', '/inbox', '/comments', '/chat-channels']
const MANAGE_PREFIXES = ['/agency', '/ctv', '/network', '/commissions']

const under = (path: string, prefix: string) => path === prefix || path.startsWith(prefix + '/')

// Trang CHỈ có ở một giao diện — giao diện đó tắt thì không vào được. Trả ship QR là việc của đơn tự bán.
const MODE_ONLY_PREFIXES: Record<WorkMode, string[]> = {
  sell: [...SELL_PREFIXES, '/ship-payments'],
  manage: MANAGE_PREFIXES,
}

/** Mở được `pathname` với các giao diện đơn vị đang bật; `modes` bỏ trống = không giới hạn (máy chủ cũ chưa trả). */
export function pathAllowedByModes(pathname: string, modes?: readonly WorkMode[] | null): boolean {
  if (!modes?.length) return true
  const p = pathname === '/' ? '/app' : pathname
  return (Object.keys(MODE_ONLY_PREFIXES) as WorkMode[]).every((m) => modes.includes(m) || !MODE_ONLY_PREFIXES[m].some((x) => under(p, x)))
}

/** Đơn vị chỉ có một giao diện → giao diện đó; có cả hai (hoặc chưa biết) → null, theo công tắc. */
export function onlyWorkMode(modes?: readonly WorkMode[] | null): WorkMode | null {
  return modes?.length === 1 ? modes[0]! : null
}

/** Chế độ mà đường dẫn thuộc về; null = trang dùng chung (giữ chế độ đang dùng). */
export function modeForPath(pathname: string): WorkMode | null {
  if (SELL_PREFIXES.some((p) => under(pathname, p))) return 'sell'
  if (MANAGE_PREFIXES.some((p) => under(pathname, p))) return 'manage'
  return null
}

/** Đại lý / CTV mới có hai chế độ. */
export function hasWorkModes(tenantType?: string | null): boolean {
  return tenantType === 'agency' || tenantType === 'ctv'
}

/** Trang mở đầu của một chế độ. */
export function homeForMode(tenantType: string | null | undefined, m: WorkMode): string {
  if (m === 'sell') return '/app'
  return tenantType === 'ctv' ? '/ctv/dashboard' : '/agency/overview'
}

export const WORK_MODE_LABEL: Record<WorkMode, string> = { manage: 'Quản lý', sell: 'Bán hàng' }
